(() => {
  'use strict';

  const FAVORITES_KEY = 'lootsepeti-favorites-v1';
  const RECENT_KEY = 'lootsepeti-recent-products-v1';
  const MAX_FAVORITES = 50;
  const MAX_RECENT = 12;


  const defaultConfig = {
    cartFlyEnabled: true,
    cartFlyDuration: 800,
    wishlistEnabled: false,
    recentEnabled: true,
    shippingEnabled: true,
    categoriesEnabled: true,
    mobileNavEnabled: true,
  };

  const shoppingConfig = (() => {
    const script = document.getElementById('LootShoppingToolsConfig');
    if (!script) return defaultConfig;
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return {
        cartFlyEnabled: parsed.cartFlyEnabled !== false,
        cartFlyDuration: Math.max(500, Math.min(1200, Number(parsed.cartFlyDuration || 800))),
        wishlistEnabled: parsed.wishlistEnabled === true,
        recentEnabled: parsed.recentEnabled !== false,
        shippingEnabled: parsed.shippingEnabled !== false,
        categoriesEnabled: parsed.categoriesEnabled !== false,
        mobileNavEnabled: parsed.mobileNavEnabled !== false,
      };
    } catch (error) {
      return defaultConfig;
    }
  })();

  let pendingCartFlight = null;
  let pendingCartFlightTimer = 0;

  const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const isVisible = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return rect.width > 8 && rect.height > 8 && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) > 0;
  };

  const findCartTarget = () => {
    const candidates = [
      document.querySelector('[data-loot-mobile-cart]'),
      document.querySelector('#cart-icon-bubble'),
      document.querySelector('.header__icon--cart'),
      document.querySelector('a[href$="/cart"]'),
    ];
    return candidates.find(isVisible) || null;
  };

  const findCartFlightImage = (form) => {
    const scopes = [
      form.closest('product-form'),
      form.closest('.product-card-wrapper'),
      form.closest('.card-wrapper'),
      form.closest('.product'),
      form.closest('quick-add-modal'),
      form.closest('.quick-add-modal'),
      form.parentElement,
    ].filter(Boolean);

    for (const scope of scopes) {
      const images = Array.from(scope.querySelectorAll('img')).filter((image) => {
        if (!isVisible(image)) return false;
        if (image.closest('.loading__spinner')) return false;
        const rect = image.getBoundingClientRect();
        return rect.width >= 40 && rect.height >= 40;
      });
      if (images.length) return images[0];
    }
    return null;
  };

  const cancelCartFlight = () => {
    window.clearTimeout(pendingCartFlightTimer);
    pendingCartFlightTimer = 0;
    pendingCartFlight = null;
  };

  const prepareCartFlight = (form) => {
    if (!shoppingConfig.cartFlyEnabled || reduceMotion()) return;
    const image = findCartFlightImage(form);
    const fallback = form.querySelector('[type="submit"]') || form;
    const source = image || fallback;
    if (!isVisible(source)) return;

    const rect = source.getBoundingClientRect();
    pendingCartFlight = {
      src: image?.currentSrc || image?.src || '',
      alt: image?.alt || '',
      rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      createdAt: Date.now(),
    };

    window.clearTimeout(pendingCartFlightTimer);
    pendingCartFlightTimer = window.setTimeout(() => runCartFlight(), 1250);
  };

  const pulseCartTarget = (target) => {
    const icon = target?.querySelector('.loot-mobile-nav__icon') || target;
    if (!icon) return;
    icon.classList.remove('loot-cart-catch');
    void icon.offsetWidth;
    icon.classList.add('loot-cart-catch');
    window.setTimeout(() => icon.classList.remove('loot-cart-catch'), 720);
  };

  function runCartFlight() {
    if (!pendingCartFlight || reduceMotion()) {
      cancelCartFlight();
      return;
    }

    const target = findCartTarget();
    if (!target) {
      cancelCartFlight();
      return;
    }

    const flightData = pendingCartFlight;
    cancelCartFlight();

    const targetRect = target.getBoundingClientRect();
    const sourceRect = flightData.rect;
    const maxSize = window.matchMedia('(max-width: 749px)').matches ? 112 : 138;
    const scale = Math.min(1, maxSize / Math.max(sourceRect.width, sourceRect.height));
    const startWidth = Math.max(42, sourceRect.width * scale);
    const startHeight = Math.max(42, sourceRect.height * scale);
    const startLeft = sourceRect.left + (sourceRect.width - startWidth) / 2;
    const startTop = sourceRect.top + (sourceRect.height - startHeight) / 2;
    const endX = targetRect.left + targetRect.width / 2 - (startLeft + startWidth / 2);
    const endY = targetRect.top + targetRect.height / 2 - (startTop + startHeight / 2);

    const flight = document.createElement('div');
    flight.className = 'loot-cart-flight';
    flight.style.left = `${startLeft}px`;
    flight.style.top = `${startTop}px`;
    flight.style.width = `${startWidth}px`;
    flight.style.height = `${startHeight}px`;

    if (flightData.src) {
      const image = document.createElement('img');
      image.src = flightData.src;
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      flight.appendChild(image);
    } else {
      flight.innerHTML = '<span aria-hidden="true">✦</span>';
    }

    document.body.appendChild(flight);

    const direction = endX >= 0 ? 1 : -1;
    const keyframes = [
      { transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)', opacity: 1, filter: 'blur(0)' },
      { transform: `translate3d(${endX * 0.18}px, ${endY * 0.08 + 18}px, 0) scale(.92) rotate(${direction * 4}deg)`, opacity: 1, offset: .22 },
      { transform: `translate3d(${endX * 0.62}px, ${endY * 0.52 + 28}px, 0) scale(.54) rotate(${direction * 13}deg)`, opacity: .92, filter: 'blur(.15px)', offset: .66 },
      { transform: `translate3d(${endX}px, ${endY}px, 0) scale(.06) rotate(${direction * 28}deg)`, opacity: .08, filter: 'blur(.7px)' },
    ];

    if (typeof flight.animate === 'function') {
      const animation = flight.animate(keyframes, {
        duration: shoppingConfig.cartFlyDuration,
        easing: 'cubic-bezier(.18,.72,.2,1)',
        fill: 'forwards',
      });
      animation.addEventListener('finish', () => {
        flight.remove();
        pulseCartTarget(target);
      }, { once: true });
      animation.addEventListener('cancel', () => flight.remove(), { once: true });
    } else {
      flight.style.transition = `transform ${shoppingConfig.cartFlyDuration}ms cubic-bezier(.18,.72,.2,1), opacity ${shoppingConfig.cartFlyDuration}ms ease`;
      requestAnimationFrame(() => {
        flight.style.transform = `translate3d(${endX}px, ${endY}px, 0) scale(.06) rotate(${direction * 28}deg)`;
        flight.style.opacity = '.08';
      });
      window.setTimeout(() => {
        flight.remove();
        pulseCartTarget(target);
      }, shoppingConfig.cartFlyDuration + 30);
    }
  }

  const readStorage = (key) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const writeStorage = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {}
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const productFromButton = (button) => ({
    id: String(button.dataset.productId || ''),
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '#',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
  });

  const getFavorites = () => readStorage(FAVORITES_KEY);
  const getRecent = () => readStorage(RECENT_KEY);

  const favoriteIds = () => new Set(getFavorites().map((item) => String(item.id)));

  const syncFavoriteButtons = () => {
    const ids = favoriteIds();
    document.querySelectorAll('[data-loot-favorite-toggle]').forEach((button) => {
      const active = ids.has(String(button.dataset.productId || ''));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('title', active ? 'Favorilerden çıkar' : 'Favorilere ekle');
      const label = button.querySelector('.loot-wishlist-button__label');
      if (label) label.textContent = active ? 'Favorilerden çıkar' : 'Favorilere ekle';
    });

    const count = ids.size;
    document.querySelectorAll('[data-loot-favorites-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  };

  const toggleFavorite = (product) => {
    if (!product.id) return;
    const favorites = getFavorites();
    const index = favorites.findIndex((item) => String(item.id) === String(product.id));
    if (index >= 0) favorites.splice(index, 1);
    else favorites.unshift(product);
    writeStorage(FAVORITES_KEY, favorites.slice(0, MAX_FAVORITES));
    syncFavoriteButtons();
    renderFavorites();
  };

  const favoriteCard = (product) => `
    <article class="loot-favorite-card">
      <a class="loot-favorite-card__image" href="${escapeHtml(product.url)}">
        ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" decoding="async" width="180" height="180">` : ''}
      </a>
      <div class="loot-favorite-card__info">
        <h3><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3>
        <div class="loot-favorite-card__price">${escapeHtml(product.price)}</div>
      </div>
      <button type="button" class="loot-favorite-card__remove" data-loot-remove-favorite="${escapeHtml(product.id)}" aria-label="Favorilerden çıkar">×</button>
    </article>`;

  function renderFavorites() {
    const list = document.querySelector('[data-loot-favorites-list]');
    const empty = document.querySelector('[data-loot-favorites-empty]');
    if (!list || !empty) return;
    const favorites = getFavorites();
    list.innerHTML = favorites.map(favoriteCard).join('');
    list.hidden = favorites.length === 0;
    empty.hidden = favorites.length !== 0;
  }

  const recentCard = (product) => `
    <article class="loot-recent-card">
      <a class="loot-recent-card__media" href="${escapeHtml(product.url)}">
        ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" decoding="async" width="480" height="480">` : ''}
      </a>
      <div class="loot-recent-card__body">
        <h3 class="loot-recent-card__title"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3>
        <div class="loot-recent-card__price">${escapeHtml(product.price)}</div>
      </div>
    </article>`;

  function renderRecent() {
    document.querySelectorAll('[data-loot-recent-section]').forEach((section) => {
      const grid = section.querySelector('[data-loot-recent-grid]');
      if (!grid) return;
      const currentId = String(section.dataset.currentProductId || '');
      const limit = Math.max(1, Number(section.dataset.limit || 6));
      const products = getRecent().filter((item) => String(item.id) !== currentId).slice(0, limit);
      grid.innerHTML = products.map(recentCard).join('');
      section.hidden = products.length === 0;
    });
    if (shoppingConfig.wishlistEnabled) syncFavoriteButtons();
  }

  const recordCurrentProduct = () => {
    const script = document.getElementById('LootCurrentProduct');
    if (!script) return;
    try {
      const product = JSON.parse(script.textContent || '{}');
      if (!product.id) return;
      const recent = getRecent().filter((item) => String(item.id) !== String(product.id));
      recent.unshift({ ...product, id: String(product.id) });
      writeStorage(RECENT_KEY, recent.slice(0, MAX_RECENT));
    } catch (error) {}
  };

  const openFavorites = (trigger) => {
    closeCategories();
    const root = document.querySelector('[data-loot-favorites-root]');
    if (!root) return;
    root._lootTrigger = trigger || document.activeElement;
    renderFavorites();
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('loot-favorites-open');
    setTimeout(() => root.querySelector('.loot-favorites__close')?.focus(), 20);
  };

  const closeFavorites = () => {
    const root = document.querySelector('[data-loot-favorites-root]');
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('loot-favorites-open');
    root._lootTrigger?.focus?.();
  };

  const openCategories = (trigger) => {
    const root = document.querySelector('[data-loot-categories-root]');
    if (!root) return;
    closeFavorites();
    root._lootTrigger = trigger || document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('loot-categories-open');
    setTimeout(() => root.querySelector('.loot-categories__close')?.focus(), 20);
  };

  const closeCategories = () => {
    const root = document.querySelector('[data-loot-categories-root]');
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('loot-categories-open');
    root._lootTrigger?.focus?.();
  };

  const updateCartCount = (count) => {
    const number = Math.max(0, Number(count || 0));
    document.querySelectorAll('[data-loot-cart-count]').forEach((badge) => {
      badge.textContent = String(number);
      badge.hidden = number === 0;
    });
  };

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} TL`;
    }
  };

  const updateRewardProgress = (cartTotal) => {
    document.querySelectorAll('[data-loot-reward-bar]').forEach((bar) => {
      const levels = [...bar.querySelectorAll('[data-loot-reward-level]')]
        .map((level) => ({
          element: level,
          index: Number(level.dataset.index || 0),
          threshold: Number(level.dataset.threshold || 0),
          title: level.dataset.title || '',
          message: level.dataset.message || '',
        }))
        .filter((level) => level.threshold > 0)
        .sort((a, b) => a.threshold - b.threshold);

      if (!levels.length) return;

      const total = Math.max(0, Number(cartTotal ?? bar.dataset.cartTotal ?? 0));
      const maximum = Math.max(...levels.map((level) => level.threshold));
      const percent = maximum > 0 ? Math.max(0, Math.min(100, (total / maximum) * 100)) : 0;
      const previousTotal = Number(bar.dataset.cartTotal || 0);
      const fill = bar.querySelector('[data-loot-reward-fill]');
      const summary = bar.querySelector('[data-loot-reward-message]');
      const surprise = bar.querySelector('[data-loot-reward-surprise]');
      const surpriseThreshold = Number(bar.dataset.thirdThreshold || 0);
      const surpriseEnabled = bar.dataset.thirdSurpriseEnabled === 'true';
      const nextLevel = levels.find((level) => total < level.threshold);

      bar.dataset.cartTotal = String(total);
      bar.classList.toggle('is-complete', !nextLevel);

      if (fill) {
        requestAnimationFrame(() => {
          fill.style.width = `${percent}%`;
        });
      }

      if (summary) {
        summary.textContent = nextLevel
          ? `${nextLevel.title} için ${formatMoney(nextLevel.threshold - total)} kaldı`
          : (bar.dataset.completeMessage || 'Tüm sepet ödüllerini kazandın!');
      }

      if (surprise) {
        const surpriseEarned = surpriseEnabled && surpriseThreshold > 0 && total >= surpriseThreshold;
        const surpriseWasEarned = surpriseEnabled && surpriseThreshold > 0 && previousTotal >= surpriseThreshold;
        surprise.hidden = !surpriseEarned;
        if (surpriseEarned && !surpriseWasEarned) {
          surprise.classList.remove('is-revealed');
          requestAnimationFrame(() => surprise.classList.add('is-revealed'));
          window.setTimeout(() => surprise.classList.remove('is-revealed'), 900);
        }
      }

      levels.forEach((level) => {
        const earned = total >= level.threshold;
        const wasEarned = previousTotal >= level.threshold;
        const state = level.element.querySelector('[data-loot-reward-state]');
        const earnedMessage = level.element.querySelector('[data-loot-reward-earned-message]');
        const marker = bar.querySelector(`[data-loot-reward-marker="${level.index}"]`);

        level.element.classList.toggle('is-earned', earned);
        marker?.classList.toggle('is-earned', earned);
        if (state) state.textContent = earned ? '✓' : String(level.index);
        if (earnedMessage) {
          earnedMessage.hidden = !earned;
          earnedMessage.textContent = level.message;
        }

        if (earned && !wasEarned) {
          level.element.classList.remove('is-reward-pop');
          requestAnimationFrame(() => level.element.classList.add('is-reward-pop'));
          window.setTimeout(() => level.element.classList.remove('is-reward-pop'), 760);
        }
      });
    });
  };

  const updateShippingProgress = (cartTotal) => {
    updateRewardProgress(cartTotal);
    document.querySelectorAll('[data-loot-shipping-progress]').forEach((bar) => {
      const threshold = Number(bar.dataset.threshold || 0);
      const total = Number(cartTotal ?? bar.dataset.cartTotal ?? 0);
      if (!threshold) return;
      const remaining = threshold - total;
      const percent = Math.max(0, Math.min(100, (total / threshold) * 100));
      bar.dataset.cartTotal = String(total);
      bar.classList.toggle('is-complete', remaining <= 0);
      const fill = bar.querySelector('[data-loot-shipping-fill]');
      const message = bar.querySelector('[data-loot-shipping-message]');
      if (fill) fill.style.width = `${percent}%`;
      if (message) message.textContent = remaining <= 0 ? 'Ücretsiz kargo kazandın!' : `Ücretsiz kargoya ${formatMoney(remaining)} kaldı`;
    });
  };

  let cartRefreshTimer = 0;
  let cartRefreshController = null;

  const refreshCartState = (delay = 50) => {
    window.clearTimeout(cartRefreshTimer);
    cartRefreshTimer = window.setTimeout(async () => {
      try {
        cartRefreshController?.abort();
        cartRefreshController = new AbortController();
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        const response = await fetch(`${cartUrl}.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: cartRefreshController.signal,
        });
        if (!response.ok) return;
        const cart = await response.json();
        const rewardBaseTotal = cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price;
        updateCartCount(cart.item_count);
        updateShippingProgress(rewardBaseTotal);
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }, delay);
  };

  const subscribeToCart = () => {
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
          const cart = event.cartData || event.data || {};
          const rewardBaseTotal = cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price;
          const hasCount = cart.item_count !== undefined;
          const hasTotal = rewardBaseTotal !== undefined;

          if (hasCount) updateCartCount(cart.item_count);
          if (hasTotal) updateShippingProgress(rewardBaseTotal);

          // Shopify /cart/add cevabı bazı ekleme akışlarında item_count içermez.
          // Üst sepet rozeti bölüm HTML'iyle yenilenirken mobil alt rozet eski kalmasın.
          if (!hasCount || !hasTotal) refreshCartState();
          if (pendingCartFlight) runCartFlight();
        });
        if (PUB_SUB_EVENTS.cartError) subscribe(PUB_SUB_EVENTS.cartError, cancelCartFlight);
      }
    } catch (error) {}
  };

  const init = () => {
    if (shoppingConfig.recentEnabled) {
      recordCurrentProduct();
      renderRecent();
    }

    if (shoppingConfig.wishlistEnabled) {
      renderFavorites();
      syncFavoriteButtons();
    }

    if (shoppingConfig.shippingEnabled || shoppingConfig.mobileNavEnabled || shoppingConfig.cartFlyEnabled || document.querySelector('[data-loot-reward-bar]')) {
      subscribeToCart();
    }

    if (document.querySelector('[data-loot-reward-bar]')) {
      updateRewardProgress();
      const rewardObserver = new MutationObserver((mutations) => {
        const rewardAdded = mutations.some((mutation) => [...mutation.addedNodes].some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches?.('[data-loot-reward-bar]') || node.querySelector?.('[data-loot-reward-bar]');
        }));
        if (rewardAdded) requestAnimationFrame(() => updateRewardProgress());
      });
      rewardObserver.observe(document.body, { childList: true, subtree: true });
    }

    if (shoppingConfig.cartFlyEnabled) {
      document.addEventListener('submit', (event) => {
        const form = event.target instanceof HTMLFormElement ? event.target : null;
        if (!form) return;
        const action = form.getAttribute('action') || '';
        if (!action.includes('/cart/add')) return;
        prepareCartFlight(form);
      }, true);
    }

    if (shoppingConfig.wishlistEnabled) {
      const mainContent = document.getElementById('MainContent');
      if (mainContent && 'MutationObserver' in window) {
        let syncFrame = 0;
        new MutationObserver((mutations) => {
          if (!mutations.some((mutation) => mutation.addedNodes.length)) return;
          cancelAnimationFrame(syncFrame);
          syncFrame = requestAnimationFrame(syncFavoriteButtons);
        }).observe(mainContent, { childList: true, subtree: true });
      }
    }

    document.addEventListener('click', (event) => {
      if (shoppingConfig.wishlistEnabled) {
        const favoriteButton = event.target.closest('[data-loot-favorite-toggle]');
        if (favoriteButton) {
          event.preventDefault();
          event.stopPropagation();
          toggleFavorite(productFromButton(favoriteButton));
          return;
        }

        const remove = event.target.closest('[data-loot-remove-favorite]');
        if (remove) {
          const favorites = getFavorites().filter((item) => String(item.id) !== String(remove.dataset.lootRemoveFavorite));
          writeStorage(FAVORITES_KEY, favorites);
          renderFavorites();
          syncFavoriteButtons();
          return;
        }
      }

      if (shoppingConfig.categoriesEnabled) {
        const categoryTrigger = event.target.closest('[data-loot-categories-open]');
        if (categoryTrigger) {
          const categoryRoot = document.querySelector('[data-loot-categories-root]');
          if (categoryRoot) {
            event.preventDefault();
            openCategories(categoryTrigger);
          }
          return;
        }

        if (event.target.closest('[data-loot-categories-close]')) {
          event.preventDefault();
          closeCategories();
          return;
        }
      }

      if (shoppingConfig.wishlistEnabled && event.target.closest('[data-loot-favorites-open]')) {
        event.preventDefault();
        openFavorites(event.target.closest('[data-loot-favorites-open]'));
        return;
      }

      if (shoppingConfig.wishlistEnabled && event.target.closest('[data-loot-favorites-close]')) {
        event.preventDefault();
        closeFavorites();
        return;
      }

      const clearRecent = shoppingConfig.recentEnabled ? event.target.closest('[data-loot-clear-recent]') : null;
      if (clearRecent) {
        writeStorage(RECENT_KEY, []);
        renderRecent();
        return;
      }

      const cartLink = event.target.closest('[data-loot-mobile-cart]');
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartLink && cartDrawer && typeof cartDrawer.open === 'function') {
        event.preventDefault();
        cartDrawer.open(cartLink);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (shoppingConfig.wishlistEnabled) closeFavorites();
      if (shoppingConfig.categoriesEnabled) closeCategories();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
